import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Home,
  Code,
  FileText,
  Settings,
  Palette,
  CreditCard,
  Wrench,
  ChevronRight,
  LogOut
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import logoDark from "../../assets/circle-logo.png";
import logoLight from "../../assets/PaperBrainz-light.png";

const Sidebar = ({ isCollapsed, isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const { theme } = useTheme();
  const { logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const logo = theme === 'dark' ? logoDark : logoLight;
  const location = useLocation();

  const menuItems = [
    { id: '/dashboard', label: 'Overview', icon: Home, path: '/dashboard' },
    { id: '/dashboard/general', label: 'General', icon: Settings, path: '/dashboard/general' },
    { id: '/dashboard/appearance', label: 'Appearance', icon: Palette, path: '/dashboard/appearance' },
    { id: '/dashboard/sources', label: 'Sources', icon: FileText, path: '/dashboard/sources' },
    { id: '/dashboard/integration', label: 'Integration', icon: Code, path: '/dashboard/integration' },
    // { id: '/dashboard/advanced', label: 'Advanced', icon: Wrench, path: '/dashboard/advanced' },
    { id: '/dashboard/billing', label: 'Billing', icon: CreditCard, path: '/dashboard/billing' },
  ];

  const handleLinkClick = () => {
    // Close mobile menu when a link is clicked
    if (setIsMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex ${isCollapsed ? 'w-14' : 'w-64'} bg-[#f7f7f5] dark:bg-[#0a0a0a] border-r border-gray-200 dark:border-white/[0.06] flex-col relative`}
        style={{
          transition: isCollapsed
            ? 'width 350ms ease-in-out 350ms'
            : 'width 350ms ease-in-out'
        }}>

        <div className={`${isCollapsed ? 'p-3' : 'p-6'}`}
          style={{
            transition: isCollapsed
              ? 'padding 350ms ease-in-out 350ms'
              : 'padding 350ms ease-in-out'
          }}>
          <a href="https://paperbrainz.com/" className="flex items-center space-x-2 mb-4 overflow-hidden">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
              <img src={logo} alt="PaperBrainz" className="w-8 h-8 object-contain rounded" />
            </div>
            <h1 className={`text-xl font-medium text-primary whitespace-nowrap ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}
              style={{
                transition: isCollapsed
                  ? 'opacity 350ms ease-in-out, width 350ms ease-in-out'
                  : 'opacity 350ms ease-in-out 350ms, width 350ms ease-in-out 350ms'
              }}>
              PaperBrainz
            </h1>
          </a>
        </div>

        <nav className={`flex-1 ${isCollapsed ? 'px-2' : 'px-4'} pb-6`}
          style={{
            transition: isCollapsed
              ? 'padding 350ms ease-in-out 350ms'
              : 'padding 350ms ease-in-out'
          }}>
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-[10px] text-[13px] font-medium group relative overflow-hidden ${
                      isActive
                        ? 'bg-black/[0.05] dark:bg-white/[0.06] text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                    }`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-4 h-4 ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'} flex-shrink-0`} />
                      <span className={`whitespace-nowrap ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'}`}
                        style={{
                          transition: isCollapsed
                            ? 'opacity 350ms ease-in-out, width 350ms ease-in-out'
                            : 'opacity 350ms ease-in-out 350ms, width 350ms ease-in-out 350ms'
                        }}>
                        {item.label}
                      </span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-gray-400 ${isCollapsed ? 'opacity-0 w-0' : isActive ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}
                      style={{
                        transition: isCollapsed
                          ? 'opacity 350ms ease-in-out, width 350ms ease-in-out'
                          : isActive
                            ? 'opacity 350ms ease-in-out 350ms, width 350ms ease-in-out 350ms'
                            : 'opacity 350ms ease-in-out, width 350ms ease-in-out'
                      }} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={`border-t border-gray-200 dark:border-white/[0.06] overflow-hidden ${isCollapsed ? 'h-0 p-0 opacity-0' : 'h-auto p-4 opacity-100'}`}
          style={{
            transition: isCollapsed
              ? 'height 350ms ease-in-out, padding 350ms ease-in-out, opacity 350ms ease-in-out'
              : 'height 350ms ease-in-out 350ms, padding 350ms ease-in-out 350ms, opacity 350ms ease-in-out 350ms'
          }}>
          <div className="text-xs text-tertiary">
            <p className="whitespace-nowrap">© 2026 <a href="https://paperbrainz.com/" className="text-blue-400 hover:text-blue-300 transition-colors">PaperBrainz</a></p>
          </div>
        </div>
      </aside>

      {/* Mobile Drawer */}
      <aside
        className={`mobile-nav-drawer flex flex-col ${isMobileMenuOpen ? 'open' : 'closed'}`}
      >
        {/* Mobile Header */}
        <div className="p-4 border-b border-gray-200 dark:border-white/[0.06]">
          <a href="https://paperbrainz.com/" className="flex items-center space-x-2 pl-12">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
              <img src={logo} alt="PaperBrainz" className="w-8 h-8 object-contain rounded" />
            </div>
            <h1 className="text-xl font-medium text-primary whitespace-nowrap">
              PaperBrainz
            </h1>
          </a>
        </div>

        {/* Mobile Navigation */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-[12px] text-[13px] font-medium group btn-touch ${
                      isActive
                        ? 'bg-black/[0.05] dark:bg-white/[0.06] text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-5 h-5 ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'} flex-shrink-0`} />
                      <span className="text-base">{item.label}</span>
                    </div>
                    {isActive && <ChevronRight className="w-5 h-5 text-gray-400" />}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Mobile Footer */}
        <div className="mt-auto border-t border-gray-200 dark:border-white/[0.06] p-4 space-y-3">
          <button
            onClick={() => setShowLogoutModal(true)}
            className="flex items-center space-x-3 px-4 py-3 rounded-[12px] text-[13px] font-medium text-red-500 hover:bg-red-500/[0.05] transition-colors btn-touch"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-base">Logout</span>
          </button>
          <div className="text-xs text-tertiary px-4">
            <p>© 2026 <a href="https://paperbrainz.com/" className="text-blue-400 hover:text-blue-300 transition-colors">PaperBrainz</a></p>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowLogoutModal(false)}
          />
          <div className="relative bg-white/80 dark:bg-[#111]/90 backdrop-blur-xl rounded-[20px] border border-gray-200 dark:border-white/[0.1] shadow-2xl w-full max-w-xs">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-500/[0.08] rounded-[14px] flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                Log out?
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-light">
                You'll need to sign in again to access your dashboard.
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-2.5 text-sm font-medium bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-full transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowLogoutModal(false); handleLinkClick(); logout(); }}
                className="flex-1 py-2.5 text-sm font-medium bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Sidebar;
